<template>
    <div>
        <div class="main-container p-4 xl:mx-8 h-30 flex lg:flex-row flex-col justify-center gap-3 lg:gap-5">
            <div
                class="default-search-container cia-search-container rounded-md p-5 bg-yellow-300 flex flex-1 flex-col"
            >
                <div class="search-container-title mb-6 flex items-center">
                    <SearchOutlined class="text-white text-4xl mr-4" />
                    <span class="text-white text-3xl">
                        {{ $t('cia.tiles.search.title') }}
                    </span>
                </div>
                <form-basic-search
                    :display="'cia'"
                    :processing="processing"
                    :search_form="search_form"
                    @formSubmit="handleFind"
                />
            </div>
            <div class="p-5 rounded-md flex flex-col lg:w-1/4 xl:w-1/5 bg-blue-500">
                <div class="container-title mb-6 flex items-center">
                    <FileAddOutlined class="text-white text-3xl mr-4" />
                    <span class="text-white text-2xl">
                        {{ $t('cia.tiles.create.title') }}
                    </span>
                </div>
                <div class="button-container create-button cia mb-4" data-cy="basic-create-container">
                    <div class="flex w-full">
                        <div class="flex flex-col w-full" data-cy="field-button-create">
                            <a-button
                                @click="$router.push({ name: 'create-cover' })"
                                class="text-left flex flex-row items-center justify-between"
                                :disabled="isAnAdmin"
                            >
                                <span>{{ $t('cia.tiles.create.button') }}</span>
                                <ArrowRightOutlined />
                            </a-button>
                        </div>
                    </div>
                </div>
                <a
                    @click="$router.push({ name: 'home-cover-search-all' })"
                    class="transition text-white underline hover:text-default-blue-green hover:no-underline hover:cursor-pointer"
                    data-cy="field-button-manage"
                >
                    {{ $t('cia.tiles.create.sub_button') }}
                </a>
            </div>
            <div class="p-5 rounded-md flex flex-col lg:w-1/4 xl:w-1/5 bg-green-500">
                <div class="container-title mb-6 flex items-center">
                    <InteractionOutlined class="text-white text-3xl mr-4" />
                    <span class="text-white text-2xl">
                        {{ $t('cia.tiles.renew.title') }}
                    </span>
                </div>
                <div class="button-container cia mb-4" data-cy="basic-renew-container">
                    <div class="flex w-full">
                        <div class="flex flex-col w-full" data-cy="field-button-renew">
                            <a-button
                                @click="$router.push({ name: 'renew-cover' })"
                                class="text-left flex flex-row items-center justify-between"
                                :disabled="isAnAdmin"
                            >
                                <span>{{ $t('cia.tiles.renew.button') }}</span>
                                <ArrowRightOutlined />
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="main-container p-4 pt-0 xl:mx-8 h-30 flex lg:flex-row flex-col gap-3 lg:gap-5">
            <div class="p-5 rounded-md flex flex-col lg:w-1/3 bg-indigo-500">
                <div class="container-title mb-6 flex items-center">
                    <FileTextOutlined class="text-white text-3xl mr-4" />
                    <span class="text-white text-2xl">
                        {{ $t('cia.tiles.vpc_report.title') }}
                    </span>
                </div>
                <div class="button-container cia mb-4" data-cy="basic-reports-container">
                    <div class="flex w-full">
                        <div class="flex flex-col w-full" data-cy="field-button-renew">
                            <a-button
                                @click="startVehiclePortfolioChecker"
                                class="text-left flex flex-row items-center justify-between"
                            >
                                <span>{{ $t('cia.tiles.vpc_report.upload_button') }}</span>
                                <UploadOutlined />
                            </a-button>
                        </div>
                    </div>
                </div>
                <a
                    @click="showVehiclePortfolioChecker"
                    class="transition text-white underline hover:text-default-blue-green hover:no-underline hover:cursor-pointer"
                    data-cy="field-button-list"
                >
                    {{ $t('cia.tiles.vpc_report.list_button') }}
                </a>
            </div>
        </div>
        <modal-vpc-upload @formSubmit="onUpload" :processing="processing" ref="UploadVPCModal" />
        <modal-vpc-list ref="ListVPCModal" />
    </div>
</template>

<script>
import FormBasicSearch from '@/components/forms/covers/BasicSearch.vue';
import ModalVpcList from '@/components/modals/VehiclePortfolioCheckerList.vue';
import ModalVpcUpload from '@/components/modals/VehiclePortfolioCheckerUpload.vue';
import {
    ArrowRightOutlined,
    FileAddOutlined,
    FileTextOutlined,
    InteractionOutlined,
    SearchOutlined,
    UploadOutlined,
} from '@ant-design/icons-vue';

export default {
    components: {
        FormBasicSearch,
        ModalVpcList,
        ModalVpcUpload,
        ArrowRightOutlined,
        FileAddOutlined,
        FileTextOutlined,
        InteractionOutlined,
        SearchOutlined,
        UploadOutlined,
    },
    computed: {
        isAnAdmin() {
            return (
                this.$store.state.user.abilities.includes('manage-association') ||
                this.$store.state.user.abilities.includes('manage-admin')
            );
        },
    },
    data() {
        return {
            search_form: {
                basic_search: '',
                certificate_no: '',
                covernote_no: '',
                reg_no: '',
                chassis_no: '',
                engine_no: '',
            },
            search_values: {},
            processing: false,
        };
    },
    methods: {
        handleFind() {
            this.search_values = this.search_form;

            if (this.search_values.basic_search == '') {
                this.$notification.error({
                    message: this.$t('covers.notifications.error.basic_empty'),
                });
                return;
            }

            this.search_values.reg_no = this.search_values.basic_search;
            this.search_values.chassis_no = this.search_values.basic_search;
            this.search_values.insurer_policy_number = this.search_values.basic_search;
            this.search_values.broker_policy_number = this.search_values.basic_search;

            this.search_values = {
                ...this.search_values,
                basic_search: '',
                years_from: 'all',
                all_match: false,
            };

            this.processSearchValues({
                ...this.search_values,
            });
        },
        processSearchValues(data) {
            this.processing = true;

            if (this.$store.getters['user/isMultiAssociation']) {
                data.association_id = this.$store.getters['user/currentAssociationId'];
            }
            this.$http
                .post('/api/covers/search', data)
                .then((response) => {
                    this.processing = false;

                    if (response.data.status == 'success') {
                        this.$router.push({
                            name: 'home-cover-search',
                            params: {
                                id: response.data.id,
                            },
                        });
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CIASH01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        startVehiclePortfolioChecker() {
            this.$refs.UploadVPCModal.showModal();
        },
        showVehiclePortfolioChecker() {
            this.$refs.ListVPCModal.showModal();
        },
        onUpload(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/reports/vehicle-porfolio-checker/upload', data, { headers })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.UploadVPCModal.fileList = [];
                        this.$refs.UploadVPCModal.modal_visible = false;

                        this.$notification.success({ message: response.data.message });
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVPCU01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
    },
};
</script>
